'use client';

import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';

export default function Contact() {
    const t = useTranslations('contact');
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });
    const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5 },
        },
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Form gönderme işlemi burada yapılacak
        setFormStatus('success');
    };

    const contactInfo = [
        {
            id: 'address',
            icon: (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            ),
        },
        {
            id: 'phone',
            icon: (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
            ),
        },
        {
            id: 'whatsapp',
            icon: (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
            ),
        },
        {
            id: 'email',
            icon: (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
            ),
        },
    ];

    return (
        <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-blue-50" ref={ref}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
                    {/* Form Bölümü */}
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate={isInView ? "visible" : "hidden"}
                        className="bg-white h-auto rounded-2xl shadow-xl p-6 sm:p-8 md:p-10"
                    >
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <motion.div variants={itemVariants}>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                    {t('form.name')}
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    required
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </motion.div>

                            <motion.div variants={itemVariants}>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    {t('form.email')}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    required
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </motion.div>

                            <motion.div variants={itemVariants}>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                    {t('form.phone')}
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    required
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </motion.div>

                            <motion.div variants={itemVariants}>
                                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                                    {t('form.message')}
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows={4}
                                    required
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </motion.div>

                            <motion.div variants={itemVariants}>
                                <button
                                    type="submit"
                                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                >
                                    {t('form.submit')}
                                </button>
                            </motion.div>

                            {formStatus !== 'idle' && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`text-center p-4 rounded-md ${formStatus === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                                        }`}
                                >
                                    {t(`form.${formStatus}`)}
                                </motion.div>
                            )}
                        </form>
                    </motion.div>

                    {/* İletişim Bilgileri */}
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate={isInView ? "visible" : "hidden"}
                        className="lg:pl-8"
                    >
                        <motion.h2
                            variants={itemVariants}
                            className="text-base text-blue-600 font-semibold tracking-wide uppercase"
                        >
                            {t('subtitle')}
                        </motion.h2>
                        <motion.h3
                            variants={itemVariants}
                            className="mt-2 text-3xl leading-8 font-bold tracking-tight text-gray-900 sm:text-4xl"
                        >
                            {t('title')}
                        </motion.h3>
                        <motion.p
                            variants={itemVariants}
                            className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto"
                        >
                            {t('description')}
                        </motion.p>

                        {contactInfo.map((item) => (
                            <motion.div
                                key={item.id}
                                variants={itemVariants}
                                className="flex items-start space-x-4 mt-8"
                            >
                                <div className="flex-shrink-0">
                                    <div className="p-3 bg-blue-600 rounded-lg text-white">
                                        {item.icon}
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-lg font-medium text-gray-900">
                                        {t(`info.${item.id}.title`)}
                                    </h4>
                                    {item.id === 'phone' && (
                                        <a
                                            href={`tel:${t(`info.${item.id}.value`)}`}
                                            className="mt-1 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                                        >
                                            {t(`info.${item.id}.value`)}
                                        </a>
                                    )}
                                    {item.id === 'whatsapp' && (
                                        <a
                                            href={`https://wa.me/${t(`info.${item.id}.value`).replace(/[^0-9]/g, '')}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-1 text-gray-600 hover:text-green-600 transition-colors duration-200"
                                        >
                                            {t(`info.${item.id}.value`)}
                                        </a>
                                    )}
                                    {item.id === 'email' && (
                                        <a
                                            href={`mailto:${t(`info.${item.id}.value`)}`}
                                            className="mt-1 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                                        >
                                            {t(`info.${item.id}.value`)}
                                        </a>
                                    )}
                                    {item.id === 'address' && (
                                        <p className="mt-1 text-gray-600">
                                            {t(`info.${item.id}.value`)}
                                        </p>
                                    )}
                                </div>
                            </motion.div>
                        ))}

                        {/* Harita */}
                        <motion.div
                            variants={itemVariants}
                            className="mt-8 h-64 rounded-lg overflow-hidden"
                        >
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12153.888267039433!2d49.85157561134787!3d40.37719712729239!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40307d6bd6211cf9%3A0x343f6b5e7ae56c6b!2sBaku%2C%20Azerbaijan!5e0!3m2!1sen!2s!4v1635959562000!5m2!1sen!2s"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen
                                loading="lazy"
                            />
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};